import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import { FormTextArea } from '../form/FormComponents'

/**
 * A JSON editor field (`type: 'json'`), first used by Studio for overlay
 * patches (30.03.08).
 *
 * The form state holds what the author typed — a string — and the server
 * parses it, so a half-typed patch is never silently replaced by the last valid
 * one and a save of broken JSON fails with the parser's message. A value that
 * arrives as an object (a record just loaded) is shown pretty-printed.
 *
 * `readOnly` renders the value as a block of formatted JSON — how Studio shows
 * diagnostics and a version's sources.
 */

const Code = styled(FormTextArea)`
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: var(--font-S);
    line-height: 1.5rem;
    white-space: pre;
    tab-size: 2;
`

const ReadOnlyBlock = styled.pre`
    margin: 0;
    padding: 0.5rem 0.75rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: var(--font-S);
    line-height: 1.4rem;
    max-height: ${({ $rows }) => `calc(1.4rem * ${$rows || 10} + 1rem)`};
    overflow: auto;
    color: ${({ theme }) => theme.form.font};
    background: ${({ theme }) => theme.form.textControl.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
`

const Status = styled.div`
    margin-top: 0.25rem;
    font-size: var(--font-XS);
    color: ${({ $error, theme }) => ($error ? '#dc3545' : theme.font.label)};
`

const textOf = (value) => {
    if (value === undefined || value === null) return ''
    if (typeof value === 'string') return value
    return JSON.stringify(value, null, 2)
}

const parseError = (text) => {
    if (text.trim() === '') return null
    try { JSON.parse(text); return null } catch (e) { return String(e?.message ?? e) }
}

const JsonInput = ({ name, value, setValue, rows = 12, readOnly = false, placeholder = '', inputId, testId, isRed = false }) => {
    const { t } = useTranslation()
    const [text, setText] = useState(() => textOf(value))
    // What this field last wrote to the form state. Any other value came from
    // outside — the record loaded or reloaded — and replaces the text.
    const lastWritten = useRef(undefined)

    useEffect(() => {
        if (value !== lastWritten.current) setText(textOf(value))
    }, [value])

    if (readOnly) {
        const shown = textOf(value)
        return <ReadOnlyBlock $rows={rows} data-testid={testId}>{shown || t('all.form.json.empty')}</ReadOnlyBlock>
    }

    const error = parseError(text)

    const handleChange = (e) => {
        const next = e.target.value
        setText(next)
        lastWritten.current = next
        setValue({ target: { name, value: next } })
    }

    return (
        <>
            <Code
                $rows={rows}
                id={inputId} name={name}
                value={text}
                onChange={handleChange}
                placeholder={placeholder}
                spellCheck={false}
                autoComplete='off'
                {...((isRed || error) && { 'aria-invalid': true })}
                data-testid={testId}
            />
            <Status $error={Boolean(error)} data-testid={testId ? `${testId}.status` : undefined}>
                {error ? `${t('all.form.json.invalid')} ${error}` : (text.trim() ? t('all.form.json.valid') : '')}
            </Status>
        </>
    )
}

export default JsonInput
